import { useEffect, useState } from 'react';
import { FileText, Download, Trash2, Pencil, Check, X, FolderOpen, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { nexaApi, docOriginalUrl } from './api';

const fmtSize = (b) => {
  if (!b && b !== 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const fmtDate = (ts) => {
  try {
    return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
  } catch { return ''; }
};

const FileRow = ({ doc, categories, onChanged, onDeleted }) => {
  const [editing, setEditing] = useState(false);
  const [cat, setCat] = useState(doc.category || 'General');
  const [name, setName] = useState(doc.filename);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await nexaApi.patch(`/docs/${doc.id}`, { category: cat.trim() || 'General', filename: name.trim() || doc.filename });
      toast.success('Updated');
      setEditing(false);
      onChanged();
    } catch { toast.error('Could not update'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${doc.filename}" from her memory? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await nexaApi.delete(`/docs/${doc.id}`);
      toast.success('Deleted');
      onDeleted(doc.id);
    } catch { toast.error('Could not delete'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3" data-testid={`nexa-file-${doc.id}`}>
      <div className="flex items-start gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-sky-400/10 flex items-center justify-center text-sky-400 shrink-0">
          <FileText className="w-4 h-4" strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          {editing ? (
            <input value={name} onChange={(e) => setName(e.target.value)}
              data-testid={`nexa-file-name-${doc.id}`}
              className="w-full bg-white/[0.05] border border-white/10 rounded-lg px-2 py-1 text-[13px] text-white outline-none focus:border-sky-400/50" />
          ) : (
            <p className="text-[13px] font-medium text-white/85 truncate">{doc.filename}</p>
          )}
          <p className="text-[11px] text-white/35 mt-0.5">
            {fmtSize(doc.size)}{doc.size != null && ' · '}{fmtDate(doc.created_at)} · {doc.chunks} sections
          </p>
        </div>
        {!editing && (
          <div className="flex items-center gap-1 shrink-0">
            {doc.has_original && (
              <a href={docOriginalUrl(doc.id)} download={doc.filename}
                data-testid={`nexa-file-download-${doc.id}`}
                className="w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center text-white/50 active:scale-90 transition-transform">
                <Download className="w-4 h-4" strokeWidth={2} />
              </a>
            )}
            <button type="button" onClick={() => setEditing(true)}
              data-testid={`nexa-file-edit-${doc.id}`}
              className="w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center text-white/50 active:scale-90 transition-transform">
              <Pencil className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
            <button type="button" onClick={remove} disabled={busy}
              data-testid={`nexa-file-delete-${doc.id}`}
              className="w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center text-rose-300/70 active:scale-90 transition-transform">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />}
            </button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="mt-2.5">
          <div className="relative">
            <FolderOpen className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" strokeWidth={2} />
            <input value={cat} onChange={(e) => setCat(e.target.value)}
              placeholder="Category" data-testid={`nexa-file-cat-input-${doc.id}`}
              className="w-full bg-white/[0.05] border border-white/10 rounded-lg pl-8 pr-2 py-1.5 text-[13px] text-white outline-none focus:border-sky-400/50" />
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {categories.filter((c) => c !== cat).map((c) => (
              <button key={c} type="button" onClick={() => setCat(c)}
                className="px-2 py-0.5 rounded-full text-[10.5px] border border-white/10 text-white/45 hover:text-white/70">
                {c}
              </button>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <button type="button" onClick={() => { setEditing(false); setCat(doc.category || 'General'); setName(doc.filename); }}
              className="flex-1 h-9 rounded-lg border border-white/10 text-white/60 text-[13px] flex items-center justify-center gap-1">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
            <button type="button" onClick={save} disabled={busy}
              data-testid={`nexa-file-save-${doc.id}`}
              className="flex-1 h-9 rounded-lg bg-sky-400 text-black font-semibold text-[13px] flex items-center justify-center gap-1">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" strokeWidth={2.5} />} Save
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-sky-400/25 bg-sky-400/10 text-sky-200/80 text-[10.5px]">
            <FolderOpen className="w-3 h-3" strokeWidth={2} /> {doc.category || 'Uncategorized'}
          </span>
        </div>
      )}
    </div>
  );
};

export const NexaFilesSection = () => {
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState('All');
  const [loading, setLoading] = useState(true);

  const load = () => {
    nexaApi.get('/docs').then(({ data }) => {
      setDocs(data.docs || []);
      setCategories(data.categories || []);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const visible = filter === 'All'
    ? docs
    : docs.filter((d) => (d.category || 'Uncategorized') === filter);
  const chips = ['All', ...categories];

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="nexa-mind-files">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-4 h-4 text-sky-400" strokeWidth={2} />
        <h3 className="text-[11px] font-mono tracking-[0.2em] text-white/50 uppercase">Files in her memory</h3>
        <span className="text-[10px] text-white/30 ml-auto">{docs.length}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-white/30" /></div>
      ) : docs.length === 0 ? (
        <p className="text-[12px] text-white/30">No files yet — attach one in chat with the paperclip and she'll remember it.</p>
      ) : (
        <>
          {chips.length > 1 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {chips.map((c) => (
                <button key={c} type="button" onClick={() => setFilter(c)}
                  data-testid={`nexa-file-filter-${c}`}
                  className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                    filter === c ? 'bg-sky-400/20 border-sky-400/50 text-sky-200'
                      : 'border-white/10 text-white/45 hover:text-white/70'
                  }`}>
                  {c}
                </button>
              ))}
            </div>
          )}
          <div className="space-y-2.5">
            {visible.map((d) => (
              <FileRow key={d.id} doc={d} categories={categories}
                onChanged={load} onDeleted={(id) => setDocs((prev) => prev.filter((x) => x.id !== id))} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
